import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BehaviorSubject, catchError, delay, delayWhen, forkJoin, map, Observable, of, retry, retryWhen, scan, Subject, switchMap, takeWhile, tap, throwError, timer } from 'rxjs';
import { DocumentLibraryConstants } from '../document-library-constants';
import { Document } from '../models/document';
import { DocumentAttachment } from '../models/document-attachment';
import { DocumentAttachmentDetails } from '../models/document-attachment-details';
import { DocumentSourceFile } from '../models/documentSourceFile';
import { DocumentSourceFileEdit } from '../models/documentSourceFileEdit';
import { TreeNodeDto } from '../models/tree-node-dto';
import { DocumentService } from './document.service';
import { DocumentSourceFileDTO } from '../models/documentSourceFileDTO';
import { LinkedDocument } from '../../shared/models/linked-documents';
import { DocumentSourceFileDetails } from '../models/documentSourceFileDetails';
import { Attachment } from '../models/attachment';
import { DocumentDetail } from '../models/documentVersionDetail';
import { PanelCardView } from '../../shared/enums/panelCardView';
import { UserReplacementDetails } from '../models/user-replacement-details';
import { BulkUserReplacementUpdateDetails } from '../models/bulk-user-replacement-update-details';
import { DocumentComment } from '../models/document-comment/document-comment';
import { SearchMapperService } from '../../shared/services/search-mapper.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentPersistenceService {

  currentTabInSliderMenu$ = new Subject<number>();
  currentTabInSliderMenuByCustomChange$ = new Subject<number>();
  closeAndResetDocumentSlider$ = new Subject<boolean>();
  documentSaveInProgress$ = new BehaviorSubject<boolean>(false);
  documentDetails$ = new BehaviorSubject<Document | null>(null);
  documentVersionDetails$ = new BehaviorSubject<DocumentDetail | null>(null);
  reasonForDocumentUpdate$ = new BehaviorSubject<string>('');
  panelListView$ = new BehaviorSubject<number>(PanelCardView.DefaultView);
  panelListScreen !: string;
  bulkUserRequirementSaveInProgress$ = new Subject<boolean>(); 
  closeAndResetBulkUserRequirementSlider$ = new Subject<boolean>();
  populateErrorsForTab$ = new BehaviorSubject<boolean>(false);
  reloadDetails$ = new Subject<boolean>();
  filesToDelete: string[] = [];
  clientSignatureAttachmentsToDelete !: string
  filesToSave: DocumentAttachment[] = [];
  clientSignatureAttachmentsToSave!: Attachment;
  documentNumber: string = '';
  documentVersion: number = 0;
  isDocumentEditSlider = false;
  savedDocumentAttachments: DocumentAttachmentDetails[] = [];
  savedClientSignatureAttachment?: Attachment;
  documentSourceFile!:DocumentSourceFileEdit;
  documentAttachments: File[] = [];
  clientSignatureAttachments : File[] = [];
  clientSignatureAttachment !: File;
  deletedDocumentAttachments: DocumentAttachmentDetails[] = [];
  deletedClientSignatureAttachment ?: Attachment;
  errorTabIndices: number[] = [];
  sourceFile!: DocumentSourceFile
  sourceFileEdit!: DocumentSourceFileEdit
  scopeList: TreeNodeDto[] = [];
  documentSourceFileDetails!: DocumentSourceFileDTO;
  documentVersionTitle?: string;
  linkedDocuments: LinkedDocument[] = [];
  uploadedResponses : any = []
  clientSignatureAttachmentVisibile : boolean = false;
  navigateToErrorTabOnSave$ = new Subject<number>();
  validatedUsersList:any=[];
  documentComments$ = new Subject<DocumentComment[] | null>();
  usersList:any=[];
  isGlobalDocument ?: boolean;
  documentDetails!: DocumentDetail;
  documentId!: string;
  isLimitedAccessMode: boolean = false;

  constructor(private documentService: DocumentService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private router: Router,
    private searchMapperService: SearchMapperService
  ) { }


  updatePanelListView(panelListView : number){
      this.panelListView$.next(panelListView);
  }

  updateScreenType(panelListScreen : string){
    this.panelListScreen = panelListScreen;
  }

  // To Show Error Tabs In Document Slider
  // To Update Error Indices On Tab Changes By Tab Menu Or Using Next/Previous Button
  updateTabErrorIndex = (tabIndex: number, isError: boolean) => {
    isError ? this.addErrorTabIndex(tabIndex) : this.removeErrorTabIndex(tabIndex);
    this.populateErrorsForTab$.next(true);
  }

  // To Add Error Index On Tab Changes By Tab Menu Or Using Next/Previous Button
  addErrorTabIndex = (tabIndex: number) => {
    if (this.errorTabIndices.indexOf(tabIndex) === -1) {
      this.errorTabIndices.push(tabIndex);
    }
  }

  // To Remove Error Index On Tab Changes By Tab Menu Or Using Next/Previous Button
  removeErrorTabIndex = (tabIndex: number) => {
    if (this.errorTabIndices.indexOf(tabIndex) > -1) {
      this.errorTabIndices.splice(this.errorTabIndices.indexOf(tabIndex), 1);
    }
  }

  onTabChange = (tabIndex: number) => {
    this.currentTabInSliderMenu$.next(tabIndex);
  }
  onCustomTabChange = (tabIndex: number) => {
    this.currentTabInSliderMenuByCustomChange$.next(tabIndex);
  }
  updateDocumentDetails = (documentDetails: Document) => {
    this.documentDetails$.next(documentDetails);
  }
  
  updateDocumentVersionDetails = (documentDetails : DocumentDetail) =>{
    this.documentVersionDetails$.next(documentDetails);
  }
  
  updatereasonforEditDocument = (reason: string) => {
    this.reasonForDocumentUpdate$.next(reason);
  }

  updateFileDetails = (documentAttachments: File[]) => {
    this.documentAttachments = documentAttachments;
  }

  updateClientSignatureAttachmentDetails = (clientSignatureAttachments: File[]) => {
    this.clientSignatureAttachments = clientSignatureAttachments;
  }
  updateRelatedDocuments = (linkedDocuments :LinkedDocument[]) => {
    this.linkedDocuments = linkedDocuments;
  }
  updateDeletedAttachments = (documentAttachments: DocumentAttachmentDetails[]) => {
    this.deletedDocumentAttachments = documentAttachments;
  }

  updateDeletedClientSignatureAttachments = (documentAttachment? : Attachment) => {
    this.deletedClientSignatureAttachment = documentAttachment;
  }
  updateSaveInProgressStatus = (status: boolean) => {
    this.documentSaveInProgress$.next(status);
  }
  updateScopeList = (scopeList: TreeNodeDto[]) => {
    this.scopeList = scopeList;
  }

  updateSourceFileDetails = (documentSourceFile: DocumentSourceFileDTO) => {
    this.documentSourceFileDetails = documentSourceFile;
  }

  updateDocumentComments = (documentComments ?: DocumentComment[]) =>{
    this.documentComments$.next(documentComments!);
  }

  updateIsGlobalDocument = (isGlobalDocument ?: boolean) =>{
    this.isGlobalDocument = isGlobalDocument;
  }

  updateIsLimitedAccessMode = (isLimitedAccessMode: boolean) => {
    this.isLimitedAccessMode = isLimitedAccessMode;
  }

  setClientSignatureAttachmentForLimitedAccess = (documentNumber: string, documentVersion: string, clientSignatureAttachment?: Attachment) => {
    this.documentNumber = documentNumber;
    this.documentVersion = parseInt(documentVersion, 10);
    this.savedClientSignatureAttachment = clientSignatureAttachment;
    this.isLimitedAccessMode = true;
  }

  reloadDocumentData = (reload: boolean = false) => {
    this.reloadDetails$.next(reload);
  }
  openConfirmationDialog = () => {
    this.confirmationService.confirm({
      message: DocumentLibraryConstants.Document_Slider_Confirmation_Text,
      header: DocumentLibraryConstants.Document_Slider_Confirmation_Header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.closeAndResetDocumentSlider$.next(true);
      }      
    });
  }
  checkSliderFromDocumentDetails() {
    this.isDocumentEditSlider = this.router.url.includes('eqms.documentDetails/');
  }

saveDocument = (): void => {
  let documentToSave = this.documentDetails$.getValue();

  if (!!documentToSave) {
      documentToSave.relatedDocuments =  this.linkedDocuments != undefined || this.linkedDocuments != null? this.linkedDocuments.map((document) => document.number) : [];
    this.documentService
      .saveDocument(documentToSave)
      .pipe(
        switchMap((documentDetails) => {
          this.documentId = documentDetails.id;
          this.documentVersionTitle = this.documentDetails$.value?.documentVersion.title;

          return this.documentService.getDocumentById(this.documentId).pipe(
            //need to revert once concrete implementation is in place
            retryWhen(errors =>
              errors.pipe(
                scan((retryCount, error) => {
                  if (retryCount >= 4) {
                    this.messageService.add({
                      severity: DocumentLibraryConstants.Severity_Err,
                      summary: DocumentLibraryConstants.Document_Creation_Failed,
                      sticky: true
                    }); 
                    this.documentSaveInProgress$.next(false);
                     throw error;  
                  }
                  return retryCount + 1;
                }, 0),
                delayWhen(() => timer(3000))
              )
            )
          );
        })
      )
      .subscribe((documentDetails) => {
        this.documentNumber = documentDetails.number;
        this.documentVersion = documentDetails.version;
        this.saveFiles();
        this.createDocumentSourceFile();
        this.updateRelatedDocuments([]);
      });
  }
}


  createDocumentSourceFile() {
    if (this.documentSourceFileDetails.isNoTemplate) {
      var fileName = this.documentSourceFileDetails.uploadedSourceFile.name;
      const sourceFileToUpload = this.buildNoTemplateSourceFileForUpload(this.documentSourceFileDetails.uploadedSourceFile);
      this.documentService.uploadFile(sourceFileToUpload, DocumentLibraryConstants.DocumentNoTemplate_UploadFolder, true)
        .pipe(
          catchError(() => {
            const validationMessage = DocumentLibraryConstants.File_Upload_Failed.replace(
              'Name',DocumentLibraryConstants.DocumentSourceFile
            );
            this.messageService.add({
              severity: DocumentLibraryConstants.Severity_Err,
              summary: validationMessage,
              sticky: true
            });

            return throwError(() => new Error('UPLOAD_SOURCE_FILE_FAILED'));
          }),
          switchMap((response) => {
          let documentSourceFileDetail : DocumentSourceFileDetails = {
            isTemplate: false,
            templateId: null,
            uploadedFileType: response.fileExtension,
            uploadedFileId: response.fileId,
            uploadedFilePath: response.filePath,
            uploadedFileName: fileName,
            documentSourceFileId: null,
            sourceFileName: this.constructDocumentSourceFileTitle(this.documentNumber, this.documentVersion,this.documentVersionTitle!)
          };
          return this.documentService.addDocumentSourceFile(documentSourceFileDetail,this.documentNumber, this.documentVersion);
        })
      )
      .subscribe({
        next:(documentSourceFileResponse)=>{
          this.documentSourceFile = documentSourceFileResponse;
        },
        error:(error: Error)=>{
          if (error.message === 'UPLOAD_SOURCE_FILE_FAILED') {
            return;
          }

          this.messageService.add({
            severity: DocumentLibraryConstants.Severity_Err,
            summary: DocumentLibraryConstants.DocumentSourceFile_Creation_Failed,
            sticky: true
          })
        }});
    }
    else if (this.documentSourceFileDetails.isTemplate) {
      let documentSourceFileDetail : DocumentSourceFileDetails = {
        isTemplate: true,
        templateId: this.documentSourceFileDetails.templateId,
        uploadedFileType: null,
        uploadedFileId: null,
        uploadedFilePath: null,
        uploadedFileName: null,
        documentSourceFileId: null,
        sourceFileName: this.constructDocumentSourceFileTitle(this.documentNumber, this.documentVersion,this.documentVersionTitle!)
      };
      this.documentService.addDocumentSourceFile(documentSourceFileDetail,this.documentNumber, this.documentVersion)
      .subscribe({
        next:(documentSourceFileResponse)=>{
        this.documentSourceFile =documentSourceFileResponse;
      },
      error:()=>{
        this.messageService.add({
          severity: DocumentLibraryConstants.Severity_Err,
          summary: DocumentLibraryConstants.DocumentSourceFile_Creation_Failed,
          sticky: true
        })
      }});
    }
  }


  updateDocument = () => {
    if (this.errorTabIndices.length === 0) {
      let documentToUpdate = this.documentDetails$.getValue();
        if (!!documentToUpdate) {
          documentToUpdate.relatedDocuments = this.linkedDocuments.map((document) => document.number);
          this.reasonForDocumentUpdate$.getValue() ? documentToUpdate.documentVersion.reasonForDocumentUpdate = this.reasonForDocumentUpdate$.getValue() : null;
          if(this.clientSignatureAttachments.length > 0 || this.savedClientSignatureAttachment != undefined 
            || this.deletedClientSignatureAttachment != undefined){
            this.saveClientSignatureAttachment(documentToUpdate);
          }
          else{
          this.documentUpdate(documentToUpdate);
        }
      } else {
        if(this.isGlobalDocument){
          if(this.linkedDocuments.length >=0){
          this.relatedDocumentsUpdate(this.linkedDocuments)
          }
        }
        else{
          if (!this.isLimitedAccessMode) {
            this.updateDocumentSourceFile();
          }
        }
        if(this.documentAttachments.length > 0 || this.deletedDocumentAttachments.length > 0){
          this.saveFiles();
        }
        if(this.clientSignatureAttachments.length > 0 || this.savedClientSignatureAttachment != undefined 
          || this.deletedClientSignatureAttachment != undefined){
          this.saveClientSignatureAttachment(this.documentDetails$.value!);
        }
      }
    } else {
      this.documentSaveInProgress$.next(false);
      this.navigateToErrorTabOnSave$.next(this.errorTabIndices[0]);
    }
  }
  
updateDocumentSourceFile() {
  if (!this.documentSourceFileDetails) {
    return;
  }

  const isNoTemplateSource = this.documentSourceFileDetails.isNoTemplate
    || (!!this.documentSourceFileDetails.uploadedSourceFile && !this.documentSourceFileDetails.isTemplate);
  const isTemplateSource = this.documentSourceFileDetails.isTemplate
    || (!!this.documentSourceFileDetails.templateId && !isNoTemplateSource);

  if (isNoTemplateSource) {
    if (!this.documentSourceFileDetails.uploadedSourceFile) {
      // In edit mode, no new file means source file is unchanged; skip update.
      if (this.isDocumentEditSlider && !!this.documentSourceFileDetails.documentSourceFileId) {
        return;
      }

      this.messageService.add({
        severity: DocumentLibraryConstants.Severity_Err,
        summary: DocumentLibraryConstants.DocumentSourceFile_Creation_Failed,
        sticky: true
      });
      return;
    }

    var fileName = this.documentSourceFileDetails.uploadedSourceFile.name;
    const sourceFileToUpload = this.buildNoTemplateSourceFileForUpload(this.documentSourceFileDetails.uploadedSourceFile);
    this.documentService.uploadFile(sourceFileToUpload, DocumentLibraryConstants.DocumentNoTemplate_UploadFolder, true)
      .pipe(
        switchMap((response) => {
          let documentSourceFileDetail: DocumentSourceFileDetails = {
            isTemplate: false,
            templateId: null,
            uploadedFileType: response.fileExtension,
            uploadedFileId: response.fileId,
            uploadedFilePath: response.filePath,
            uploadedFileName: fileName,
            documentSourceFileId: this.documentSourceFileDetails.documentSourceFileId,
            sourceFileName: this.constructDocumentSourceFileTitle(this.documentNumber, this.documentVersion, this.documentVersionTitle!)
          };

          return this.documentService.updateDocumentSourceFile(
            documentSourceFileDetail,
            this.documentNumber,
            this.documentVersion
          );
        })
      )
      .subscribe({
        next: (documentSourceFileResponse) => {
          this.documentSourceFile = documentSourceFileResponse;
        },
        error: (error: Error) => {
          this.messageService.add({
            severity: DocumentLibraryConstants.Severity_Err,
            summary: error.message,
            sticky: true
          });
        }
      });
    return;
  }

  if (isTemplateSource) {
    let documentSourceFileDetail: DocumentSourceFileDetails = {
      isTemplate: true,
      templateId: this.documentSourceFileDetails.templateId,
      uploadedFileType: null,
      uploadedFileId: null,
      uploadedFilePath: null,
      uploadedFileName: null,
      documentSourceFileId: this.documentSourceFileDetails.documentSourceFileId,
      sourceFileName: this.constructDocumentSourceFileTitle(this.documentNumber, this.documentVersion, this.documentVersionTitle!)
    };

    this.documentService.updateDocumentSourceFile(documentSourceFileDetail, this.documentNumber, this.documentVersion)
      .subscribe({
        next: (documentSourceFileResponse) => {
          this.documentSourceFile = documentSourceFileResponse;
        },
        error: (error: Error) => {
          this.messageService.add({
            severity: DocumentLibraryConstants.Severity_Err,
            summary: error.message,
            sticky: true
          });
        }
      });
    return;
  }

  // Nothing to update for source file in this state.
  return;
}

  showConfirmationMessage = (severityMessage: string, summaryMessage: string) => {
    this.messageService.add({
      severity: severityMessage,
      summary: summaryMessage,
    });
  }

  saveClientSignatureAttachment(document : Document){
    let deleteClientSignatureObservable = of(false);
    let addClientSignatureObservable : Observable<any>= of(null);
    if(this.clientSignatureAttachments.length > 0){
      this.clientSignatureAttachment = this.clientSignatureAttachments.find(a=>a)!;
    }
    if (this.deletedClientSignatureAttachment != null 
      && this.deletedClientSignatureAttachment != undefined) {
        deleteClientSignatureObservable = this.documentService
        .deleteFile(this.deletedClientSignatureAttachment.attachmentFileId).pipe(
        catchError(error => {
          console.error('Error deleting client signtaure attachment', error);
          return of(false);
        })
      );
    }
    if (this.clientSignatureAttachment !== null &&
       this.clientSignatureAttachment !== undefined) {
        var documentDetails = this.documentNumber +'-v'+ this.documentVersion
        const modifiedFile = new File([this.clientSignatureAttachment], documentDetails + '/' + this.clientSignatureAttachment.name,
           { type: this.clientSignatureAttachment.type });
        addClientSignatureObservable = this.documentService
        .uploadFile(modifiedFile, 'clientSignatureAttachment')
        .pipe(
          catchError(error => {
            console.error('Error while uploading client signature for document', error);
            const validationMessage = DocumentLibraryConstants.File_Upload_Failed.replace(
              'Name',DocumentLibraryConstants.ClientSignatureAttachment
            );
            this.messageService.add({
              severity: DocumentLibraryConstants.Severity_Err,
              summary: validationMessage,
              sticky: true
            })
            return of(null);
          }));
       }

    forkJoin({
      deleteOperation: deleteClientSignatureObservable,
      addOperation: addClientSignatureObservable
    }).subscribe({
      next: (results) => {
        // LIMITED ACCESS: Build attachment directly without document object
        if (this.isLimitedAccessMode) {
          let attachmentToUpdate: Attachment | undefined = undefined;

          // Preserve existing attachment if no changes
          if(this.savedClientSignatureAttachment !== null 
            && this.savedClientSignatureAttachment !== undefined){
              attachmentToUpdate = this.constructSavedClientSignatureAttachment();
          }
          
          // Handle deletion
          if(results.deleteOperation){
            attachmentToUpdate = undefined;
          }
          
          // Handle new upload
          if(results.addOperation != null){
            const newAttachment = this.constructClientSignatureAttachment(this.clientSignatureAttachment, results.addOperation);
            if(newAttachment.attachmentFileId != this.clientSignatureAttachmentsToDelete){
              attachmentToUpdate = newAttachment;
            }
          }

          // Call simplified endpoint
          if(this.isDocumentEditSlider){
            this.documentService.updateClientSignatureAttachment(
              this.documentNumber,
              this.documentVersion,
              attachmentToUpdate
            ).pipe(
              catchError((err) => {
                this.messageService.add({
                  severity: DocumentLibraryConstants.Severity_Err,
                  summary: `${err.error}`,
                });
                this.documentSaveInProgress$.next(false);
                return throwError(() => err);
              })
            ).subscribe(() => {
              this.updateSaveInProgressStatus(false);
              this.showConfirmationMessage(
                DocumentLibraryConstants.Severity_Success,
                `${DocumentLibraryConstants.Document_Updated_Success} ${this.documentNumber}`
              );
              this.reloadPage();
            });
          }
        } else {
          if(this.savedClientSignatureAttachment !== null 
            && this.savedClientSignatureAttachment !== undefined){
              document.documentVersion.clientSignatureAttachment = 
              this.constructSavedClientSignatureAttachment();
            }
          if(results.deleteOperation){
           document.documentVersion.clientSignatureAttachment = undefined
          }
          if(results.addOperation != null){
            this.clientSignatureAttachmentsToSave = 
            this.constructClientSignatureAttachment(this.clientSignatureAttachment, results.addOperation);
            if(this.clientSignatureAttachmentsToSave.attachmentFileId != this.clientSignatureAttachmentsToDelete){
              document.documentVersion.clientSignatureAttachment = this.clientSignatureAttachmentsToSave;
            }
          }
          
          if(this.isDocumentEditSlider){
            this.documentUpdate(document);
          }
        }
      },
      error: (error) => {
        const validationMessage = DocumentLibraryConstants.File_Upload_Failed.replace(
          'Name',DocumentLibraryConstants.ClientSignatureAttachment
        );
        this.messageService.add({
          severity: DocumentLibraryConstants.Severity_Err,
          summary: validationMessage,
          sticky: true
        })
        console.error('Error while saving document:', error);
      },
    });

  }

  documentUpdate(document: Document){
    this.documentService
    .updateDocument(document)
    .pipe(
      catchError((err) => {
        this.messageService.add({
          severity: DocumentLibraryConstants.Severity_Err,
          summary: `${err.error}`,
        });
        this.documentSaveInProgress$.next(false);
        return throwError(() => err);
      })
    ) 
    .subscribe((documentDetails) => {
      this.documentNumber = documentDetails.number;
      this.documentVersion = documentDetails.document.documentVersion.version;
      this.documentVersionTitle = this.documentDetails$.value?.documentVersion.title
      if(this.documentAttachments.length > 0 || this.deletedDocumentAttachments.length > 0){
        this.saveFiles();
      } else {
        this.documentAttachments = [];
        this.deletedDocumentAttachments = [];
        this.updateSaveInProgressStatus(false);
        this.showConfirmationMessage(DocumentLibraryConstants.Severity_Success,
          `${DocumentLibraryConstants.Document_Updated_Success} ${this.documentNumber}`);
        if(!this.isDocumentEditSlider) {
          this.openDocumentDetails(this.documentNumber);
        }      
      }
      if(!this.isGlobalDocument){
        this.updateDocumentSourceFile();
      }
      this.updateRelatedDocuments([]);
    });
  }

  saveFiles = () => {
    this.filesToSave = [];
    let successMessage = `${DocumentLibraryConstants.Document_Created_Success}`;
    let files = this.documentAttachments;

    const saveUploadedFiles = () => {
      if (files.length === 0) {
        this.updateSaveInProgressStatus(false);
        this.showConfirmationMessage(DocumentLibraryConstants.Severity_Success,
          `${successMessage} ${this.documentNumber}`);
        this.documentAttachments = [];
        this.deletedDocumentAttachments = [];
        this.isDocumentEditSlider ? this.reloadPage() : this.openDocumentDetails(this.documentNumber);
      } else {
        var isUploadFailed = false;
        var hasSavedAttachments = false;
        var documentDetails = this.documentNumber +'-v'+ this.documentVersion
        files.forEach((file: File, index :number) => {
          const modifiedFile = new File([file], documentDetails + '/' + file.name, { type: file.type });
          this.documentService.uploadFile(modifiedFile, 'documentAttachments')
          .subscribe({
            next:(response) => {
            this.filesToSave.push(this.constructDocumentAttachment(file, response));
            if (this.filesToSave.length === files.length && !hasSavedAttachments) {
              hasSavedAttachments = true;
              this.documentService.saveDocumentAttachments(this.filesToSave).subscribe(() => {
                if(index == files.length - 1 && isUploadFailed){
                  const validationMessage = DocumentLibraryConstants.File_Upload_Failed.replace(
                    'Name',DocumentLibraryConstants.DocumentAttachments
                  );
                  this.messageService.add({
                    severity: DocumentLibraryConstants.Severity_Err,
                    summary: validationMessage,
                    sticky: true
                  })
                }
                this.updateSaveInProgressStatus(false);
                this.showConfirmationMessage(DocumentLibraryConstants.Severity_Success,
                  `${successMessage} ${this.documentNumber}`);
                this.documentAttachments = [];
                this.deletedDocumentAttachments = [];
                this.isDocumentEditSlider ? this.reloadPage() : this.openDocumentDetails(this.documentNumber);
              })
            }
          },
        error:()=>{
          isUploadFailed = true
          if(index == files.length - 1 && isUploadFailed){
          const validationMessage = DocumentLibraryConstants.File_Upload_Failed.replace(
            'Name',DocumentLibraryConstants.DocumentAttachments
          );
          this.messageService.add({
            severity: DocumentLibraryConstants.Severity_Err,
            summary: validationMessage,
            sticky: true
          })
          this.updateSaveInProgressStatus(false);
          this.showConfirmationMessage(DocumentLibraryConstants.Severity_Success,
            `${successMessage} ${this.documentNumber}`);
          this.documentAttachments = [];
          this.deletedDocumentAttachments = [];
          this.isDocumentEditSlider ? this.reloadPage() : this.openDocumentDetails(this.documentNumber);
        }}});

        });
      }
    };

    if (this.isDocumentEditSlider) {
      successMessage = `${DocumentLibraryConstants.Document_Updated_Success}`;
      this.deleteFiles().subscribe(() => {
        saveUploadedFiles();
      });
      return;
    }

    saveUploadedFiles();

  }
deleteFiles(): Observable<void> {
  const deletedFiles = this.deletedDocumentAttachments;

  if (!deletedFiles || deletedFiles.length === 0) {
    return of(void 0);
  }

  return forkJoin(
    deletedFiles.map(item =>
      this.documentService.deleteFile(item.fileId)
    )
  )
    .pipe(
      switchMap(results => {
        const filesToDelete = deletedFiles
          .filter((_, index) => results[index])
          .map(item => item.documentAttachmentId || '');

        return filesToDelete.length
          ? this.documentService.deleteDocumentAttachments(filesToDelete).pipe(map(() => void 0))
          : of(void 0);
      })
    );
}
deleteClientSignatureAttachment(document: Document): void {
  const deletedFile = this.deletedClientSignatureAttachment;

  if (!deletedFile) {
    return;
  }

  this.documentService
    .deleteFile(deletedFile.attachmentFileId)
    .pipe(
      tap((isDeleted) => {
        if (isDeleted) {
          this.clientSignatureAttachmentsToDelete =
            deletedFile.attachmentFileId || '';
        }

        document.documentVersion.clientSignatureAttachment = undefined;
      })
    )
    .subscribe(() => {
      if (this.isDocumentEditSlider) {
        this.documentUpdate(document);
      }
    });
}


  constructDocumentAttachment = (file: File, fileSaveResponse: any): DocumentAttachment => {
    return {
      fileId: fileSaveResponse.fileId,
      documentNumber: this.documentNumber,
      documentVersion: this.documentVersion,
      attachmentName: file.name,
      attachmentType: file.name.split(".").pop() || ' ',
      attachmentPath: fileSaveResponse.filePath,
      createdBy: '',
      createdDate: new Date()
    }
  }

  constructClientSignatureAttachment = (file: File, fileSaveResponse: any): Attachment => {
    return {
      attachmentFileId: fileSaveResponse.fileId,
      attachmentName: file.name,
      attachmentType: file.name.split(".").pop() || ' ',
      attachmentPath: fileSaveResponse.filePath,
      createdBy: '',
      createdOn: new Date()
    }
  }
  updateSavedDocuments = (documentAttachments: DocumentAttachmentDetails[]) => {
    this.savedDocumentAttachments = documentAttachments;
  }

  updateSavedClientSignatureAttachments = (clientSignatureAttachments : Attachment) => {
    this.savedClientSignatureAttachment = clientSignatureAttachments;
  }
  openDocumentDetails = (documentNumber: string) => {
    this.closeAndResetDocumentSlider$.next(true);
    setTimeout(() => {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([ 
          '/eqms.documentDetails/' + documentNumber + ':1',
        ])
      );
      window.open(url, '_blank');
    }, 1000);
  }
  // ToDo : Need to update the component values instead of loading page
  reloadPage = () => {
    setTimeout(() => {
      this.reloadDocumentData(true);
      this.closeAndResetDocumentSlider$.next(true);
    }, 1000);
  }
  constructSavedClientSignatureAttachment = (): Attachment => {
    return {
      attachmentFileId: this.savedClientSignatureAttachment?.attachmentFileId || '',
      attachmentName: this.savedClientSignatureAttachment?.attachmentName,
      attachmentType: this.savedClientSignatureAttachment?.attachmentType,
      attachmentPath: this.savedClientSignatureAttachment?.attachmentPath,
      createdBy: this.savedClientSignatureAttachment?.createdBy,
      createdOn: this.savedClientSignatureAttachment?.createdOn
    }
  }

  constructDocumentSourceFileTitle = (documentNumber: string,documentVersion : number, versionTitle:string) => {
    console.log(documentNumber + " - " + versionTitle.replace(/[<>?\/\\:|*\"]/g,""));
    return documentNumber+"-v"+documentVersion + " - " + versionTitle.replace(/[<>?\/\\:|*\"]/g,"");
  }

  private buildNoTemplateSourceFileForUpload = (sourceFile: File): File => {
    const sourceFileTitle = this.constructDocumentSourceFileTitle(
      this.documentNumber,
      this.documentVersion,
      this.documentVersionTitle || ''
    );
    const extension = sourceFile.name.includes('.') ? sourceFile.name.split('.').pop() : '';
    const uniqueFileName = extension ? `${sourceFileTitle}.${extension}` : sourceFileTitle;
    return new File([sourceFile], uniqueFileName, { type: sourceFile.type });
  }

  relatedDocumentsUpdate = (linkedDocuments: LinkedDocument[]) => {
    var relatedDocumentNumbers = linkedDocuments.map((document) => document.number);
    this.documentService.updateRelatedDocuments(this.documentNumber, this.documentVersion, relatedDocumentNumbers)
    .subscribe(() => {
      this.updateSaveInProgressStatus(false);
      if(this.savedDocumentAttachments.length == 0 || this.deletedDocumentAttachments.length == 0){
      let successMessage = `${DocumentLibraryConstants.Document_Updated_Success}`;
      this.showConfirmationMessage(DocumentLibraryConstants.Severity_Success,
        `${successMessage} ${this.documentNumber}`);
      }
      if(!this.isDocumentEditSlider) {
        this.openDocumentDetails(this.documentNumber);
      }
    })
  }
  UpdateBulkUserRequirementForDocument = (noOfDocuments: number, userReplacementDetails: UserReplacementDetails) => {
    this.confirmationService.confirm({
      message: DocumentLibraryConstants.BulkUserReplacementUpdate_Message.replace( "{noOfDocuments}", noOfDocuments.toString()),
      header: DocumentLibraryConstants.Document_Slider_Confirmation_Header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        const transformedPayload = this.buildBulkUserReplacementPayload(userReplacementDetails);
        
        this.documentService.updateBulkUserForDocument(transformedPayload).pipe(
          catchError((err) => {
            this.messageService.add({
              severity: DocumentLibraryConstants.Severity_Err,
              summary: `${err.error}`,
            });
            return throwError(() => err);
          })
        ) 
        .subscribe((response: BulkUserReplacementUpdateDetails) => {
          if(response !== null && response !== undefined){
            if(this.isValidArray(response.validationOnLoggedInUser)){
              this.messageService.add({
                severity: DocumentLibraryConstants.Severity_Err,
                summary: DocumentLibraryConstants.Error_BulkUserReplacement_ValidationOnLoggedInUser.replace( "{documentNumberAndTitle}", this.prepareErrorMessage(response.validationOnLoggedInUser)),
                sticky: true
              });
            }

            if(this.isValidArray(response.validationOnUserToAdd)){
              this.messageService.add({
                severity: DocumentLibraryConstants.Severity_Err,
                summary: DocumentLibraryConstants.Error_BulkUserReplacement_ValidationOnUserToAdd.replace( "{documentNumberAndTitle}", this.prepareErrorMessage(response.validationOnUserToAdd)),
                sticky: true
              });
            }

            if(this.isValidArray(response.validationOnUserToRemove)){
              this.messageService.add({
                severity: DocumentLibraryConstants.Severity_Err,
                summary: DocumentLibraryConstants.Error_BulkUserReplacement_ValidationOnUserToRemove.replace( "{documentNumberAndTitle}", this.prepareErrorMessage(response.validationOnUserToRemove)),
                sticky: true
              });
            }

            if(this.isValidArray(response.validationOnSelectedAssignments)){
              this.messageService.add({
                severity: DocumentLibraryConstants.Severity_Err,
                summary: DocumentLibraryConstants.Error_BulkUserReplacement_ValidationOnSelectedAssignmentsUnavailable.replace( "{documentNumberAndTitle}", this.prepareErrorMessage(response.validationOnSelectedAssignments)),
                sticky: true
              });
            }
            if(this.isValidArray(response.validationOnPhaseOfDocumentVersion)){
              this.messageService.add({
                severity: DocumentLibraryConstants.Severity_Err,
                summary: DocumentLibraryConstants.Error_BulkUserReplacement_ValidationOnPhaseOfDocumentVersion.replace( "{documentNumberAndTitle}", this.prepareErrorMessage(response.validationOnPhaseOfDocumentVersion)),
                sticky: true
              });
            }
            if(this.isValidArray(response.validationOnReviewerAndApproverBeingSame)){
              this.messageService.add({
                severity: DocumentLibraryConstants.Severity_Err,
                summary: DocumentLibraryConstants.Error_BulkUserReplacement_ValidationOnReviewerAndApproverBeingSame.replace( "{documentNumberAndTitle}", this.prepareErrorMessage(response.validationOnReviewerAndApproverBeingSame)),
                sticky: true
              });
            }
            if(this.isValidArray(response.validationOnAuthor)){
              this.messageService.add({
                severity: DocumentLibraryConstants.Severity_Err,
                summary: DocumentLibraryConstants.Error_BulkUserReplacement_ValidationOnAuthor.replace( "{documentNumberAndTitle}", this.prepareErrorMessage(response.validationOnAuthor)),
                sticky: true
              });
            }
            if(this.isValidArray(response.validationOnPhaseForReviewerAndApprover)){
              this.messageService.add({
                severity: DocumentLibraryConstants.Severity_Err,
                summary: DocumentLibraryConstants.Error_BulkUserReplacement_ValidationOnPhaseForReviewerAndApprover.replace( "{documentNumberAndTitle}", this.prepareErrorMessage(response.validationOnPhaseForReviewerAndApprover)),
                sticky: true
              });
            }

            if(response.updateSuccessMessage !== null && response.updateSuccessMessage !== undefined && response.updateSuccessMessage !== ""){
              this.messageService.add({
                severity: DocumentLibraryConstants.Severity_Success,
                summary: response.updateSuccessMessage,
                sticky: true
              });

              //Information Trigger
              this.messageService.add({
                severity: DocumentLibraryConstants.Severity_Info,
                summary: DocumentLibraryConstants.BulkUserReplacementUpdate_Info_Message,
                sticky: true
              });

            }
          }
          this.closeAndResetBulkUserRequirementSlider$.next(true);
        });
      },      
    });
  }

  isValidArray(value: any): boolean {
    return value !== null && value !== undefined && value.length > 0;
  }

  prepareErrorMessage(value: any) : string {
    let items = "";
    if(value !== null && value !== undefined)
      items = value.length > 1 ? value?.join(", ") : value;

    return items;
  }

  closeConfirmationDialogForBulkUpdate = () => {
    this.confirmationService.confirm({
      message: DocumentLibraryConstants.Document_Slider_Confirmation_Text,
      header: DocumentLibraryConstants.Document_Slider_Confirmation_Header,
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        this.closeAndResetBulkUserRequirementSlider$.next(true);
      }      
    });
  }

  private buildBulkUserReplacementPayload(details: UserReplacementDetails): any {
    const basePayload: any = {
      isSelectAcrossAllPages: details.isSelectAcrossAllPages ?? false,
      userToAdd: details.userToAdd,
      userToRemove: details.userToRemove ?? null,
      assignments: details.assignments ?? []
    };

    if (details.isSelectAcrossAllPages) {
      // For select-all mode, we need to build the filter with primarySearchText (not from currentFilters.searchText)
      // Create a copy of currentFilters without the searchText field to avoid mapping it to Filter.SearchText
      const filtersWithoutSearchText = details.currentFilters ? 
        { ...details.currentFilters, searchText: undefined } : null;
      
      const searchRequest = this.searchMapperService.mapToBaseSearchRequest(
        filtersWithoutSearchText,
        details.currentSort,
        details.currentPagination
      );

      // Now set the primary search text explicitly
      searchRequest.Filter = searchRequest.Filter || { SearchText: null, FilterItems: [] };
      searchRequest.Filter.SearchText = details.primarySearchText || null;

      return {
        ...basePayload,
        filter: searchRequest.Filter,
        sort: searchRequest.Sort,
        pagination: searchRequest.Pagination,
        secondarySearchText: details.secondarySearchText ?? null,
        deselectedDocumentNumberAndVersion: details.deselectedDocumentNumberAndVersion ?? [],
        documentNumberAndVersion: undefined
      };
    } else {
      // REGULAR MODE
      return {
        ...basePayload,
        filter: null,
        sort: null,
        pagination: {
          PageNumber: details.currentPagination?.pageNumber ?? 1,
          PageSize: details.currentPagination?.pageSize ?? 25
        },
        secondarySearchText: null,
        documentNumberAndVersion: details.documentNumberAndVersion ?? [],
        deselectedDocumentNumberAndVersion: undefined
      };
    }
  }

  
  conversionOfDateIntoLocalDateTime(date : Date): Date {
    const now = new Date();
    date.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
    return date;
  }
}

